/**
 * 📡 CATÁLOGO FORMAL DE EVENTOS REALTIME — PR-19
 *
 * Fuente de verdad para nombres de eventos Socket.io en el frontend MGI.
 * Importar desde aquí en lugar de usar strings hardcodeados.
 *
 * Estructura:
 *   RT          — Eventos server → client (backend emite, frontend escucha en socket)
 *   RT_CLIENT   — Eventos client → server (frontend emite al backend)
 *   RT_LOCAL    — Eventos locales re-emitidos por websocketService (emitLocal/emit)
 *
 * Uso:
 *   import { RT, RT_LOCAL } from '@/services/realtime-events'
 *   websocketService.on(RT_LOCAL.INCIDENCE_UPDATE, handler)
 */

// =============================================
// SERVER → CLIENT  (backend emite por socket)
// =============================================

export const RT = {
  // --- Protocolo realtime (pub/sub) ---
  REALTIME_DASHBOARD_UPDATE:  'realtime:dashboard_update',
  REALTIME_INCIDENCE_UPDATE:  'realtime:incidence_update',
  REALTIME_ASSIGNMENT_UPDATE: 'realtime:assignment_update',
  REALTIME_GROUP_UPDATE:      'realtime:group_update',
  REALTIME_NOTIFICATION:      'realtime:notification',

  // --- Conexión / Handshake ---
  CONNECTION_ESTABLISHED:     'connection:established',
  SUBSCRIBE_ROOMS_RESULT:     'subscribe:rooms:result',

  // --- Auth ---
  AUTH_FORCE_LOGOUT:          'auth:force_logout',

  // --- Incidencias ---
  INCIDENT_STATUS_CHANGED:    'incident:status-changed',
  INCIDENT_DEPARTMENT_ASSIGNED: 'incident:department-assigned',
  INCIDENT_UPDATED:           'incident:updated',
  INCIDENT_CREATED:           'incident:created',
  // P13-V6B@V1 — invalidación mínima para la audiencia previa (assign/transfer/delete)
  INCIDENT_ACCESS_REVOKED:    'incident:access-revoked',
  // P13-V1C — señal de autoridad del claim atómico: {incidenceId, action(CLAIMED|ASSIGNED|RELEASED),
  // timestamp}. Señal, no estado: el consumidor refetchea la autoridad canónica (trailing edge).
  INCIDENT_HANDLER_CHANGED:   'incident:handler-changed',

  // --- Comentarios ---
  COMMENT_CREATED:            'comment:created',
  COMMENT_UPDATED:            'comment:updated',
  COMMENT_DELETED:            'comment:deleted',

  // --- Inventario ---
  // P13-V2B (PD-V2-04 / K4A): los 14 nombres de eventos de dominio de inventario fueron
  // PURGADOS — todos eran huérfanos (cero productores tras el retiro K4A; el bridge del
  // websocket.service jamás los suscribió). La señal de inventario es la notificación durable
  // K4B por `realtime:notification` (ver INVENTORY_NOTIFICATION_CONTRACTS abajo) y la
  // frescura de listas es QUERY_DRIVEN_REFETCH.

  // --- Rutas ---
  ROUTES_CHANGED:             'routes:changed',
  ROUTE_EXECUTIONS_CHANGED:   'route-executions:changed',
  ROUTE_OPERATORS_CHANGED:    'route-operators:changed',
  ROUTE_INCIDENTS_CHANGED:    'route-incidents:changed',

  // --- Agenda ---
  EVENTS_CHANGED:             'events:changed',

  // --- Analytics ---
  ANALYTICS_METRICS:          'analytics:metrics',
  ANALYTICS_TIMESERIES:       'analytics:timeseries',

  // --- Sistema ---
  SYSTEM_BROADCAST:           'system:broadcast',
  SYSTEM_MAINTENANCE:         'system:maintenance',
} as const

// =============================================
// CLIENT → SERVER  (frontend emite al backend)
// =============================================

export const RT_CLIENT = {
  JOIN_ROOM:                'join-room',
  LEAVE_ROOM:               'leave-room',
  SUBSCRIBE:                'realtime:subscribe',
  UNSUBSCRIBE:              'realtime:unsubscribe',
  PUBLISH:                  'realtime:publish',
  SUBSCRIBE_ROOMS:          'subscribe:rooms',
  ANALYTICS_GET_METRICS:    'analytics:get_metrics',
  ANALYTICS_GET_TIMESERIES: 'analytics:get_timeseries',
} as const

// =============================================
// LOCAL EVENTS  (re-emitidos por websocketService)
// Estos NO salen al socket — son internos del frontend
// =============================================

export const RT_LOCAL = {
  // Lifecycle del socket
  SOCKET_CONNECT:        'socket:connect',
  SOCKET_DISCONNECT:     'socket:disconnect',
  SOCKET_CONNECT_ERROR:  'socket:connect_error',
  SOCKET_RECONNECT:      'socket:reconnect',
  SOCKET_RECONNECT_ERROR: 'socket:reconnect_error',

  // Mapeados desde protocolo realtime (1:1 con RT pero nombre local diferente)
  DASHBOARD_UPDATE:      'dashboard:update',
  INCIDENCE_UPDATE:      'incidence:update',
  ASSIGNMENT_UPDATE:     'assignment:update',
  GROUP_UPDATE:          'group:update',
  NOTIFICATION_RECEIVED: 'notification:received',

  // Incidencias (pasados directamente)
  INCIDENT_STATUS_CHANGED: 'incident:status-changed',
  INCIDENT_CREATED:        'incident:created',
  INCIDENT_DEPARTMENT_ASSIGNED: 'incident:department-assigned',
  INCIDENT_ACCESS_REVOKED: 'incident:access-revoked',
  INCIDENT_HANDLER_CHANGED: 'incident:handler-changed',

  // Inventario: PURGADO (P13-V2B) — ver el comentario en RT; ningún consumidor local queda.

  // Rutas (pasados directamente)
  ROUTES_CHANGED:           'routes:changed',
  ROUTE_OPERATORS_CHANGED:  'route-operators:changed',
  ROUTE_EXECUTIONS_CHANGED: 'route-executions:changed',
  ROUTE_INCIDENTS_CHANGED:  'route-incidents:changed',

  // Agenda (pasado directamente)
  EVENTS_CHANGED:           'events:changed',

  // Comentarios (pasados directamente)
  COMMENT_CREATED:          'comment:created',
  COMMENT_UPDATED:          'comment:updated',
  COMMENT_DELETED:          'comment:deleted',

  // Analytics (pasados directamente)
  ANALYTICS_METRICS:        'analytics:metrics',
  ANALYTICS_TIMESERIES:     'analytics:timeseries',

  // Sistema (pasados directamente)
  SYSTEM_BROADCAST:         'system:broadcast',
  SYSTEM_MAINTENANCE:       'system:maintenance',
} as const

// =============================================
// P13-V2B (V6B@V2, PD-V2-04) — Avisos comerciales de Inventario
// =============================================
// El inventario NO tiene eventos realtime de dominio (los 15 nombres K4A retirados quedan
// retirados). La señal visible es la notificación DURABLE K4B que viaja por el canal genérico
// `realtime:notification`; el frontend la reconoce por el marcador persistido
// payload.originalType = 'inventory_commercial' y enruta por payload.data.contract, y su única
// reacción es el REFETCH canónico (jamás materializar estado desde el payload).

export const INVENTORY_NOTIFICATION_CONTRACTS = [
  'inventory.request.created',
  'inventory.request.approved',
  'inventory.request.rejected',
  'inventory.request.delivered',
  'inventory.request.cancelled',
  'inventory.outbound.dispatched',
  'inventory.outbound.delivered',
  'inventory.outbound.cancelled',
] as const

export type InventoryNotificationContract = typeof INVENTORY_NOTIFICATION_CONTRACTS[number]

/**
 * Reconoce un aviso comercial de Inventario en cualquiera de las dos formas que llegan al
 * cliente (evento del worker K4B: `{ payload: { originalType, data: { contract } } }`;
 * fila sincronizada por API: `{ type: 'inventory_commercial', data: { contract } }`).
 * Devuelve el contrato canónico o null.
 */
export function inventoryCommercialContractOf(data: any): InventoryNotificationContract | null {
  const p = data?.payload ?? data
  const originalType = p?.originalType ?? data?.originalType ?? data?.type
  if (originalType !== 'inventory_commercial') return null
  const contract = p?.data?.contract ?? data?.data?.contract ?? null
  return (INVENTORY_NOTIFICATION_CONTRACTS as readonly string[]).includes(contract) ? contract : null
}

// =============================================
// Tipos derivados
// =============================================

export type RTEvent       = typeof RT[keyof typeof RT]
export type RTClientEvent = typeof RT_CLIENT[keyof typeof RT_CLIENT]
export type RTLocalEvent  = typeof RT_LOCAL[keyof typeof RT_LOCAL]
